import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CdwOuCollectionPickerModule,
  OuCollectionPickerValue,
} from '@cdw-ae/cdw-ou-collection-picker';
import {
  AccessManagementDesignationRecord,
  AccessManagementOrgUnitCollectionRecord,
  AccessManagementRoleRecord,
  AccessManagementUserCollectionRecord,
  UserRecord,
} from '@cdw-ae/common-data';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwAutocompleteModule } from '@cdw-ae/common-ui-components-autocomplete';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import { CdwMessageBandModule } from '@cdw-ae/common-ui-components-message-band';
import { CdwSelectModule } from '@cdw-ae/common-ui-components-select';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import { CdwSelectOption } from '@cdw-ae/shared-interfaces';

// Type definitions for user/collection selections
interface UserSelectionItem {
  type: 'USER' | 'COLLECTION';
  id: number;
  displayName?: string;
}

@Component({
  selector: 'lib-cdw-designation-edit',
  templateUrl: './cdw-designation-edit.component.html',
  styleUrls: ['./cdw-designation-edit.component.scss'],
  imports: [
    CommonModule,
    MaterialLibraryModule,
    CommonUiComponentsTextInputModule,
    CdwMessageBandModule,
    CdwButtonModule,
    CdwDividerModule,
    CdwCheckboxModule,
    CdwSelectModule,
    CdwAutocompleteModule,
    CdwOuCollectionPickerModule,
  ],
})
export class CdwDesignationEditComponent implements OnInit {
  readonly grey400Color = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-gray-400')
    .trim();

  readonly grey300Color = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-gray-300')
    .trim();

  @Input() designation!: AccessManagementDesignationRecord | undefined;

  @Input() users: UserRecord[] = [];

  @Input() userCollections: AccessManagementUserCollectionRecord[] = [];

  @Input() roles: AccessManagementRoleRecord[] = [];

  @Input() ous: TreeNode[] = [];

  @Input() ouCollections: AccessManagementOrgUnitCollectionRecord[] = [];

  @Output() cancelDesignationEdit = new EventEmitter();

  @Output() submitDesignationEdit = new EventEmitter();

  @Output() deleteDesignation = new EventEmitter();

  @Output() openDetailsDrawer = new EventEmitter<{
    type: string;
    id: number | string;
    name?: string;
  }>();

  roleSelectOptions: CdwSelectOption[] = [];

  userSelectOptions: CdwSelectOption[] = [];

  designationForm!: FormGroup;

  selectedUser: UserSelectionItem | null = null;
  selectedUsers: UserSelectionItem[] = [];

  selectedRole: number | null = null;
  selectedRoles: number[] = [];

  get userSelectionDisplayText(): string {
    // Multi-select mode: show counts
    if (this.selectedUsers && this.selectedUsers.length > 0) {
      const userCount = this.selectedUsers.filter(
        (u) => u.type === 'USER'
      ).length;
      const collectionCount = this.selectedUsers.filter(
        (u) => u.type === 'COLLECTION'
      ).length;

      const parts = [];
      if (userCount > 0) {
        parts.push(`${userCount} user${userCount > 1 ? 's' : ''}`);
      }
      if (collectionCount > 0) {
        parts.push(
          `${collectionCount} user collection${collectionCount > 1 ? 's' : ''}`
        );
      }

      return parts.length > 0 ? `${parts.join(' and ')} selected` : '';
    }

    // Single-select mode: show the name
    if (!this.selectedUser) return '';

    if (this.selectedUser.type === 'COLLECTION') {
      return `${this.selectedUser.displayName} (user collection)`;
    } else {
      return this.selectedUser.displayName || '';
    }
  }

  get ouSelectionDisplayText(): string {
    const ousValue = this.designationForm?.get('ous')
      ?.value as OuCollectionPickerValue | null;
    if (!ousValue) return '';

    // Single OU collection selected
    if (ousValue.collectionIds && ousValue.collectionIds.length > 0) {
      const collectionId = ousValue.collectionIds[0];
      const collection = this.ouCollections.find((c) => c.id === collectionId);
      return collection ? `${collection.name} (OU Collection)` : '';
    }

    // Single OU selected - need to find the OU name from the tree
    if (ousValue.ous && ousValue.ous.length > 0) {
      const ouId = ousValue.ous[0];
      const ouName = this.findOuNameById(ouId, this.ous);
      return ouName || '';
    }

    return '';
  }

  get roleSelectionDisplayText(): string {
    if (this.selectedRoles.length > 1) {
      return `${this.selectedRoles.length} roles selected`;
    }
    if (this.selectedRoles.length === 1) {
      const role = this.roles.find((r) => r.id === this.selectedRoles[0]);
      return role ? role.name : '';
    }
    if (!this.selectedRole) return '';
    const role = this.roles.find((r) => r.id === this.selectedRole);
    return role ? role.name : '';
  }

  constructor(
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  private findOuNameById(ouId: string, nodes: TreeNode[]): string | null {
    for (const node of nodes) {
      if (node.value === ouId) {
        return node.displayName;
      }
      if (node.children) {
        const found = this.findOuNameById(ouId, node.children);
        if (found) return found;
      }
    }
    return null;
  }

  ngOnInit(): void {
    // Build role options with details action
    this.roleSelectOptions = this.roles.map((role) => ({
      displayName: role.name,
      value: role.id,
      showDetailsAction: {
        label: 'See details',
        handler: (option: CdwSelectOption) => {
          this.openDetailsDrawer.emit({
            type: 'role',
            id: option.value as number,
            name: option.displayName,
          });
        },
      },
    }));

    // Build user and user collection options with details action
    this.userSelectOptions = [
      ...this.userCollections.map((user) => ({
        displayName: `${user.name} (user collection)`,
        value: { ...user, type: 'COLLECTION' },
        showDetailsAction: {
          label: 'See details',
          handler: (option: CdwSelectOption) => {
            const collectionData =
              option.value as AccessManagementUserCollectionRecord & {
                type: string;
              };
            this.openDetailsDrawer.emit({
              type: 'userCollection',
              id: collectionData.id,
              name: user.name,
            });
          },
        },
      })),
      ...this.users.map((user) => ({
        displayName: user.email,
        value: { ...user, type: 'USER' },
        // Users don't have a details drawer, so no action
      })),
    ];

    if (this.designation) {
      // For editing, load single selection.
      //
      // Carry the option's displayName onto the value so the
      // userSelectionDisplayText getter can render the entity name in the
      // collapsed picker. Without this, the value object only has
      // {id, type, ...userRecordFields} (no displayName) and the placeholder
      // computes to '' on USER and 'undefined (user collection)' on
      // COLLECTION, leaving the picker visually blank on edit.
      let selectedUserValue: UserSelectionItem | null = null;
      if (this.designation.userId) {
        const userOption = this.userSelectOptions.find(
          (userSelectOption) =>
            (userSelectOption.value as UserRecord).id ===
              this.designation?.userId &&
            (userSelectOption.value as { type: string }).type === 'USER'
        );
        if (userOption)
          selectedUserValue = {
            ...(userOption.value as UserSelectionItem),
            displayName: userOption.displayName,
          };
      }
      if (this.designation.userCollectionId) {
        const collectionOption = this.userSelectOptions.find(
          (userSelectOption) =>
            (userSelectOption.value as AccessManagementUserCollectionRecord)
              .id === this.designation?.userCollectionId &&
            (userSelectOption.value as { type: string }).type === 'COLLECTION'
        );
        if (collectionOption)
          selectedUserValue = {
            ...(collectionOption.value as UserSelectionItem),
            // Strip the " (user collection)" suffix already baked into the
            // option's displayName — the getter re-adds it when type is
            // COLLECTION, so the raw collection name is what we want here.
            displayName: (
              collectionOption.value as AccessManagementUserCollectionRecord
            ).name,
          };
      }

      // Build OU picker initial value from designation
      const ouPickerValue: OuCollectionPickerValue = {
        ous: this.designation.orgUnitId ? [this.designation.orgUnitId] : [],
        collectionIds: this.designation.orgUnitCollectionId
          ? [this.designation.orgUnitCollectionId]
          : [],
      };

      const selectedRoleValue = this.designation.roleId
        ? this.designation.roleId
        : null;

      this.designationForm = this.formBuilder.group({
        ous: [ouPickerValue, Validators.required],
        users: [selectedUserValue, Validators.required],
        roles: [selectedRoleValue, Validators.required],
      });

      this.selectedUser = selectedUserValue;
      this.selectedRole = selectedRoleValue;
      this.selectedRoles = selectedRoleValue ? [selectedRoleValue] : [];
    } else {
      const emptyOuPickerValue: OuCollectionPickerValue = {
        ous: [],
        collectionIds: [],
      };
      this.designationForm = this.formBuilder.group({
        ous: [emptyOuPickerValue, Validators.required],
        users: [null, Validators.required],
        roles: [null, Validators.required],
      });
    }
  }

  public onUserSelectionDone(selectedOptions: CdwSelectOption[]): void {
    // Handle multiple selections
    this.selectedUsers = selectedOptions.map(
      (opt) => opt.value as UserSelectionItem
    );

    this.selectedUser =
      selectedOptions.length > 0 && selectedOptions[0]?.value
        ? (selectedOptions[0].value as UserSelectionItem)
        : null;

    // Store array for multi-select or single for single-select
    const formValue =
      selectedOptions.length > 1 ? this.selectedUsers : this.selectedUser;

    this.designationForm.patchValue({ users: formValue });
    // Trigger change detection to update the display text
    this.changeDetectorRef.detectChanges();
  }

  public onRoleSelectionDone(selectedOptions: CdwSelectOption[]): void {
    // Handle multiple selections
    this.selectedRoles = selectedOptions
      .filter((opt) => opt.value !== null && opt.value !== undefined)
      .map((opt) => opt.value as number);

    // For backward compatibility, also set single selection (first item)
    this.selectedRole =
      selectedOptions.length > 0 &&
      selectedOptions[0]?.value !== null &&
      selectedOptions[0]?.value !== undefined
        ? (selectedOptions[0].value as number)
        : null;

    // Store array for multi-select or single for single-select
    const formValue =
      selectedOptions.length > 1 ? this.selectedRoles : this.selectedRole;

    this.designationForm.patchValue({ roles: formValue });
    // Trigger change detection to update the display text
    this.changeDetectorRef.detectChanges();
  }

  public onDeleteDesignation() {
    this.deleteDesignation.emit();
  }

  public onSaveDesignation() {
    if (this.designationForm?.controls) {
      const selectedUserValue = this.designationForm.get('users')
        ?.value as UserSelectionItem | null;
      const selectedOusValue = (this.designationForm.get('ous')
        ?.value as OuCollectionPickerValue) || { ous: [], collectionIds: [] };

      // Process single user selection
      let userId: number | null = null;
      let userCollectionId: number | null = null;

      if (selectedUserValue) {
        if (selectedUserValue.type === 'USER') {
          userId = selectedUserValue.id;
        } else if (selectedUserValue.type === 'COLLECTION') {
          userCollectionId = selectedUserValue.id;
        }
      }

      // OU selections come directly from the picker (single selection)
      const orgUnitId =
        selectedOusValue.ous && selectedOusValue.ous.length > 0
          ? selectedOusValue.ous[0]
          : null;
      const orgUnitCollectionId =
        selectedOusValue.collectionIds &&
        selectedOusValue.collectionIds.length > 0
          ? selectedOusValue.collectionIds[0]
          : null;

      const designationData: {
        roleIds: number[];
        orgUnitCollectionId: number | null;
        orgUnitId: string | null;
        userCollectionId: number | null;
        userId: number | null;
        type: number;
      } = {
        roleIds: this.selectedRoles,
        type: 1, // Designation type
        orgUnitId,
        orgUnitCollectionId,
        userId,
        userCollectionId,
      };

      this.submitDesignationEdit.emit(designationData);
    }
  }

  public onCancelDesignation() {
    this.cancelDesignationEdit.emit();
  }

  public isFormValid(): boolean {
    const usersValue = this.designationForm.get('users')
      ?.value as UserSelectionItem | null;
    const ousValue = this.designationForm.get('ous')
      ?.value as OuCollectionPickerValue | null;

    const hasOuSelection =
      (ousValue?.ous?.length ?? 0) > 0 ||
      (ousValue?.collectionIds?.length ?? 0) > 0;

    return (
      this.designationForm?.valid &&
      usersValue !== null &&
      this.selectedRoles.length > 0 &&
      hasOuSelection
    );
  }
}
